import { useId, useRef, useState } from 'react'
import { workspaceService } from '../services/workspaceService'
import { useWorkspaceStore } from '../stores/workspaceStore'
import { useUser } from '@clerk/clerk-react'
import { Alert, Button, Field, Modal, Stack } from '../ds'

interface CreateWorkspaceModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const CreateWorkspaceModal = ({ isOpen, onClose, onSuccess }: CreateWorkspaceModalProps) => {
  const [workspaceName, setWorkspaceName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  
  const formId = useId()
  const nameRef = useRef<HTMLInputElement>(null)

  const { currentAccount, switchWorkspace } = useWorkspaceStore()
  const { user } = useUser()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!workspaceName.trim() || !currentAccount) {
      setError('Workspace name is required')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const newWorkspace = await workspaceService.createWorkspace(currentAccount.id, {
        name: workspaceName.trim()
      }, {
        clerkUserId: user?.id,
        name: user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || undefined,
        firstName: user?.firstName || undefined,
        lastName: user?.lastName || undefined
      })
      // `null`: the session that issued this create ended while it was in flight (sign-out, a
      // user switch). Nothing was recorded, so there is nothing to switch to or report.
      if (!newWorkspace) return

      // Reload workspace data to refresh the list
      if (user?.id) {
        await workspaceService.loadWorkspaceData(user.id, true, user.publicMetadata as Record<string, unknown>)
      }
      
      // Switch to the newly created workspace
      switchWorkspace(newWorkspace.id)

      setWorkspaceName('')
      onSuccess?.()
      onClose()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create workspace')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setWorkspaceName('')
      setError('')
      onClose()
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Create New Workspace"
      size="md"
      dismissible={!isLoading}
      initialFocus={nameRef}
      actions={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button
            type="submit"
            form={formId}
            disabled={!workspaceName.trim()}
            busy={isLoading}
            busyLabel="Creating..."
          >
            Create Workspace
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        <Stack>
          <Field
            label="Workspace Name"
            value={workspaceName}
            onChange={(e) => setWorkspaceName((e.target as HTMLInputElement).value)}
            placeholder="e.g., Production Store, Test Environment"
            disabled={isLoading}
            controlRef={nameRef}
          />
          {error && <Alert tone="danger" live="assertive">{error}</Alert>}
        </Stack>
      </form>
    </Modal>
  )
}

export default CreateWorkspaceModal
